/* Looking at a sub's document without saving it (Duncan 2026-10-22: "there also is no way to view the document
   currently uploaded. We need a preview button or something to look at the current uploaded document"): its file,
   fetched with the sign-in its route needs (a plain link can't send it), shown here -- a PDF in the browser's own
   viewer, a photo as itself -- with Open in a new tab and Download. A Word or Excel file the browser can't show is
   for downloading. DocPreview shows a file the same way right on a page (2026-10-28, an application's W-9 and safety
   plan beside its answers). */
import React, { useEffect, useState } from 'react';
import { Icon } from '../ui';
import { ModalShell, ErrorBanner, PrimaryButton, SecondaryButton } from './SubUi';
import { fetchSubcontractorDocument } from '../../api';

// What a browser can show: by the type it was stored with, else by its name (an upload can come in untyped).
const SHOWN_BY_EXT = { pdf: 'application/pdf', png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', gif: 'image/gif', webp: 'image/webp' };
const SHOWN_TYPES = new Set(Object.values(SHOWN_BY_EXT));
function shownTypeOf(blob, filename) {
  const t = String(blob.type || '').toLowerCase().split(';')[0].trim();
  if (SHOWN_TYPES.has(t)) return t;
  return SHOWN_BY_EXT[String(filename || '').toLowerCase().split('.').pop()] || null;
}

const note = { padding: '14px 16px', borderRadius: 10, background: 'var(--bg-surface-2)', border: '1px solid var(--border-subtle)', fontSize: 14, color: 'var(--text-2)' };

/** `doc`'s file, fetched and made something to show: loading | ready { url, type } | unshowable | error { error }. */
function useShownFile(doc, fetchBlob) {
  const [view, setView] = useState({ status: 'loading' });
  useEffect(() => {
    let live = true;
    let url = null;
    setView({ status: 'loading' });
    fetchBlob(doc)
      .then((blob) => {
        if (!live) return;
        const type = shownTypeOf(blob, doc.filename);
        if (!type) return setView({ status: 'unshowable' });
        url = URL.createObjectURL(blob.type === type ? blob : new Blob([blob], { type }));
        return setView({ status: 'ready', url, type });
      })
      .catch((e) => { if (live) setView({ status: 'error', error: e.message || 'Couldn’t open the file' }); });
    return () => { live = false; if (url) URL.revokeObjectURL(url); };
  }, [doc]);
  return view;
}

/** The file itself, `height` tall: a PDF in the browser's viewer, a picture as itself, else why it isn't shown. */
function Shown({ doc, view, height }) {
  if (view.status === 'loading') return <div role="status" style={note}>Opening {doc.filename}…</div>;
  if (view.status === 'error') return <ErrorBanner>{view.error}</ErrorBanner>;
  if (view.status === 'unshowable') return <div style={note}>Summit can’t show this kind of file here. Download it to open it.</div>;
  return view.type === 'application/pdf' ? (
    <iframe src={view.url} title={`${doc.filename}, shown`}
      style={{ display: 'block', width: '100%', height, border: '1px solid var(--border-subtle)', borderRadius: 8, background: '#fff' }} />
  ) : (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', height, overflow: 'auto', padding: 8, boxSizing: 'border-box', borderRadius: 8, background: 'var(--bg-surface-2)' }}>
      <img src={view.url} alt={doc.filename} style={{ maxWidth: '100%', maxHeight: height === '100%' ? 'none' : height, objectFit: 'contain' }} />
    </div>
  );
}

/** The viewer: `doc` (a subcontractor document), what it is in `subtitle`; Download saves it (onDownload). `fetchBlob`
    reads another kind of file (an application's W-9: 2026-10-27); a subcontractor document's by default. */
export default function DocViewer({ doc, subtitle, onDownload, onClose, fetchBlob = fetchSubcontractorDocument }) {
  const view = useShownFile(doc, fetchBlob);
  return (
    <ModalShell title={doc.filename} subtitle={subtitle} onClose={onClose} width={1040}
      footer={(
        <>
          {/* A real link, not window.open: a browser can refuse to open the file's in-memory address in a window
              opened without its opener; a link the person clicks always goes. */}
          {view.status === 'ready' && (
            <a href={view.url} target="_blank" rel="noopener noreferrer" style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, height: 34, padding: '0 13px', borderRadius: 8,
              fontSize: 14, fontWeight: 500, whiteSpace: 'nowrap', fontFamily: 'var(--font-sans)', color: 'var(--text-2)', textDecoration: 'none',
              background: 'var(--bg-surface)', border: '1px solid var(--border-default)', boxShadow: 'var(--shadow-xs)',
            }}><Icon name="external-link" size={14} />Open in a new tab</a>
          )}
          <SecondaryButton onClick={() => onDownload(doc)}><Icon name="download" size={14} />Download</SecondaryButton>
          <PrimaryButton onClick={onClose}>Close</PrimaryButton>
        </>
      )}>
      <Shown doc={doc} view={view} height="70vh" />
    </ModalShell>
  );
}

/** A file shown right on the page, filling the box it's in (`doc`, read with `fetchBlob`). */
export function DocPreview({ doc, fetchBlob = fetchSubcontractorDocument }) {
  const view = useShownFile(doc, fetchBlob);
  return <Shown doc={doc} view={view} height="100%" />;
}
